import { Text } from '@fluentui/react-components';
import type { DurationSource, ReportingTimeBasis } from '../../utils/callDisplay';
import type { CallLeg } from '../../types';
import {
  durationCoverageLabel,
  durationSourceLabel,
  formatCallDuration,
  reportingTimeLabel,
} from '../../utils/callDisplay';

export function ReportingTimeDisplay({
  value,
  basis,
  format,
}: {
  value: string | null;
  basis: ReportingTimeBasis | null;
  format: (value: string | null) => string;
}) {
  return (
    <span style={{ display: 'inline-block' }}>
      <span style={{ whiteSpace: 'nowrap' }}>{format(value)}</span>
      {/* On its own line -- otherwise the value's trailing "AM"/"PM" runs
          straight into the basis caption's leading word (e.g. "AMStarted"). */}
      <Text size={200} block style={{ whiteSpace: 'nowrap' }}>{reportingTimeLabel(basis)}</Text>
    </span>
  );
}

export function CallLegReportingTimeDisplay({
  leg,
  format,
}: {
  leg: Pick<CallLeg, 'reporting_time' | 'reporting_time_basis'>;
  format: (value: string | null) => string;
}) {
  return (
    <ReportingTimeDisplay
      value={leg.reporting_time}
      basis={leg.reporting_time_basis}
      format={format}
    />
  );
}

export function DurationValueDisplay({
  value,
  source,
  format,
}: {
  value: number | null;
  source: DurationSource | null;
  format: (value: number | null) => string;
}) {
  const sourceLabel = durationSourceLabel(source);
  return (
    <span>
      <span>{format(value)}</span>
      {sourceLabel && <small style={{ display: 'block' }}>{sourceLabel}</small>}
    </span>
  );
}

export function DurationCoverageNote({
  knownCount,
  totalCount,
  isPartial,
}: {
  knownCount: number;
  totalCount: number;
  isPartial: boolean;
}) {
  const label = durationCoverageLabel(knownCount, totalCount, isPartial);
  return label ? <span>{label}</span> : null;
}

export function DurationAggregateDisplay({
  averageSeconds,
  knownCount,
  totalCount,
  isPartial,
  completionFallbackCount = 0,
}: {
  averageSeconds: number | null;
  knownCount: number;
  totalCount: number;
  isPartial: boolean;
  completionFallbackCount?: number;
}) {
  const coverage = durationCoverageLabel(knownCount, totalCount, isPartial);
  return (
    <span>
      Avg Duration: <strong>{formatCallDuration(averageSeconds)}</strong>
      {coverage && <small style={{ display: 'block' }}>{coverage}</small>}
      {completionFallbackCount > 0 && (
        <small style={{ display: 'block' }}>
          {completionFallbackCount.toLocaleString()} {completionFallbackCount === 1 ? 'call' : 'calls'} reported by completion time
        </small>
      )}
    </span>
  );
}
